import React from 'react';
import { MediafilePoster } from '@entities/media';
import type { MediafilePosterTone } from '@entities/media';
import FileUtils from 'application/utils/FileUtils';
import { getCoverKey } from 'application/components/utils';
import { TEXTS } from '../../../texts';
import { DEFAULT_IMAGE_DURATION } from '../../../consts';
import { formatMillisecondsToTime } from '../../../utils';
import { resolveMediafilePreviewResource } from '../../resolveMediafilePreviewResource';
import { FALLBACK_COVER_COUNT, FALLBACK_COVER_TITLE } from '../consts';
import type { MediafilePosterAdapterProps } from './types';

const MediafilePosterAdapter: React.FC<MediafilePosterAdapterProps> = ({ file, kind, onPreview }) => {
  if (file.itemType === 'emenu') {
    return (
      <MediafilePoster
        id={file.id}
        state="ready"
        kind="emenu"
        durationLabel={file.playDuration ? formatMillisecondsToTime(file.playDuration) : undefined}
      />
    );
  }

  const title = file.title ?? '';
  const previewResource = resolveMediafilePreviewResource(file);
  const imageUrl = previewResource?.kind === 'image' ? previewResource.uri : undefined;
  const thumbnailUrl = file.previewUrl || imageUrl;
  const thumbnail = thumbnailUrl ? { src: thumbnailUrl, fallbackSrc: imageUrl, alt: '' } : undefined;
  const fallbackTone = getCoverKey(title || FALLBACK_COVER_TITLE, FALLBACK_COVER_COUNT) as MediafilePosterTone;
  const formatLabel = FileUtils.getOriginalMediafileSource(file.sourcefiles)?.extension ?? undefined;
  const durationLabel = formatMillisecondsToTime(file.duration || file.playDuration || DEFAULT_IMAGE_DURATION);
  const preview = previewResource
    ? {
        label: `${TEXTS.PREVIEW}: ${title}`,
        onRequest: (event: React.MouseEvent<HTMLButtonElement>) => onPreview(file.id, event.currentTarget),
      }
    : undefined;

  return kind === 'video' ? (
    <MediafilePoster
      id={file.id}
      state="ready"
      kind="video"
      thumbnail={thumbnail}
      fallbackTone={fallbackTone}
      formatLabel={formatLabel}
      durationLabel={durationLabel}
      preview={preview}
    />
  ) : (
    <MediafilePoster
      id={file.id}
      state="ready"
      kind="image"
      thumbnail={thumbnail}
      fallbackTone={fallbackTone}
      formatLabel={formatLabel}
      durationLabel={durationLabel}
      preview={preview}
    />
  );
};

export default MediafilePosterAdapter;
